import { Folder, Layer } from "../../../types/publication";

/**
 * The layer list's tree (root layers/folders, each folder holding its own layers) is rendered and
 * dragged as ONE single flat `Reorder.Group` in `layer-list.tsx`, instead of a `Reorder.Group` per
 * folder. This is what lets a layer or folder be dragged smoothly across folder boundaries, and
 * what lets a folder itself be reordered relative to root-level layers/other folders.
 *
 * The flow is:
 *   1. `buildFlatOrder` turns the tree into a flat list of ids for the `Reorder.Group`'s `values`
 *      prop: root layers/folders in order, and - for each OPEN folder - its child layer ids right
 *      after it, followed by an invisible end-marker id (`getFolderEndMarkerId`). Closed folders
 *      contribute only their own id (their children aren't rendered/draggable at all).
 *   2. `buildRenderRows` produces the matching flat list of rows to actually render, in the same
 *      order/membership as `buildFlatOrder`, via a single top-level `.map()` (see its own comment
 *      for why nested per-folder `.map()`s aren't used).
 *   3. Framer Motion drags/swaps ids around within that flat list, calling `onReorder` with the new
 *      flat id order. `checkReorder` (framer-motion internals) only ever performs ONE adjacent swap
 *      per call, so a single continuous drag across a folder boundary fires many `onReorder` calls,
 *      each moving the dragged id one step further through the flat list.
 *   4. `reconstructOrder` walks that flat id list back into `{ id, folderId? }` pairs: an id sits
 *      between an open folder's header and its end-marker id, it's a child of that folder; a
 *      folder header itself is always root-level (folders can't be nested). This is also where a
 *      folder header that transiently ends up "inside" another still-open folder's range mid-drag
 *      (before `checkReorder`'s one-swap-at-a-time has fully carried it past) is handled without
 *      corrupting that folder's membership - see its own comment for details.
 *   5. The store mutator `createReorderFlatLayers` rebuilds the whole layer tree from that
 *      `{ id, folderId? }` list in one pass. It's also told which folders were actually expanded as
 *      part of THIS traversal (`expandedFolderIds`) so it can tell "this folder's children weren't
 *      touched because it wasn't part of the flat list at all" (e.g. transiently drag-collapsed,
 *      see `layer-list.tsx`) apart from "this folder was genuinely emptied by the drag".
 */

const FOLDER_END_PREFIX = "folder-end:";

export function getFolderEndMarkerId(folderId: string) {
  return `${FOLDER_END_PREFIX}${folderId}`;
}

function getFolderIdFromEndMarker(id: string): string | null {
  return id.startsWith(FOLDER_END_PREFIX) ? id.slice(FOLDER_END_PREFIX.length) : null;
}

/**
 * Step 1: turns the tree into the flat id list used as the `Reorder.Group`'s `values` (see the
 * module comment above). Root layers/folders in order, and for each OPEN folder its child ids
 * followed by an end-marker id - closed folders just contribute their own id.
 */
export function buildFlatOrder(layers: Array<Layer | Folder>): string[] {
  const ids: string[] = [];
  for (const item of layers) {
    ids.push(item.id);
    if (item.type === "folder" && item.opened) {
      for (const child of item.layers) {
        if (child.type === "layer") ids.push(child.id);
      }
      ids.push(getFolderEndMarkerId(item.id));
    }
  }
  return ids;
}

export type RenderRow =
  | { kind: "folder-header"; folder: Folder }
  | { kind: "layer"; layer: Layer; folder?: Folder }
  | { kind: "folder-empty"; folder: Folder }
  | { kind: "folder-end"; folder: Folder };

/**
 * Step 2: the flat rows actually rendered, matching `buildFlatOrder`'s order/membership. Rendered
 * via a single top-level `.map()` (no nested per-folder `.map()`s) so an item keeps its component
 * instance - and any in-progress drag - when it moves in or out of a folder (see module comment).
 */
export function buildRenderRows(layers: Array<Layer | Folder>): RenderRow[] {
  const rows: RenderRow[] = [];
  for (const item of layers) {
    if (item.type === "layer") {
      rows.push({ kind: "layer", layer: item });
      continue;
    }

    rows.push({ kind: "folder-header", folder: item });
    if (!item.opened) continue;

    const childLayers = item.layers.filter((child): child is Layer => child.type === "layer");
    if (childLayers.length === 0) {
      rows.push({ kind: "folder-empty", folder: item });
    } else {
      for (const child of childLayers) {
        rows.push({ kind: "layer", layer: child, folder: item });
      }
    }
    rows.push({ kind: "folder-end", folder: item });
  }
  return rows;
}

/**
 * Step 4: turns a dragged flat id list back into `{ id, folderId? }` pairs, using the end-marker
 * ids to know when a folder's children stop (see module comment). Folder headers always end up at
 * root, since folders can't be nested.
 *
 * One wrinkle: mid-drag, a folder header can transiently pass through another still-open folder's
 * rows (framer-motion only swaps one adjacent pair per call, so it takes several `onReorder` calls
 * to fully clear it). We don't let that sighting close the surrounding folder's context, otherwise
 * everything after it would wrongly fall out of that folder for that tick.
 */
export function reconstructOrder(
  newIds: string[],
  folderOpenById: Map<string, boolean>
): Array<{ id: string; folderId?: string }> {
  const order: Array<{ id: string; folderId?: string }> = [];
  let currentFolderId: string | undefined;

  for (const id of newIds) {
    const markerFolderId = getFolderIdFromEndMarker(id);
    if (markerFolderId) {
      if (currentFolderId === markerFolderId) currentFolderId = undefined;
      continue;
    }

    if (folderOpenById.has(id)) {
      order.push({ id });
      // Don't reopen/reset the context if one's already active - see docstring above.
      if (currentFolderId === undefined) {
        currentFolderId = folderOpenById.get(id) ? id : undefined;
      }
      continue;
    }

    order.push({ id, folderId: currentFolderId });
  }

  return order;
}

/**
 * Step 5: pure rebuild of a layer tree from a flat `{ id, folderId? }` order (as produced by
 * `reconstructOrder` above) plus the original tree it was derived from - used both to actually
 * commit a drag reorder (`createReorderFlatLayers` in `mutators-move.ts`) and, while a drag is
 * still in progress, to render a live preview without touching the store at all (see
 * `layer-list.tsx`, which only commits to the store once a drag gesture ends, to avoid mutating
 * the store - and everything subscribed to it, like the map - on every intermediate drag frame).
 */
export function rebuildLayersFromOrder(
  rootLayers: Array<Layer | Folder>,
  order: Array<{ id: string; folderId?: string }>,
  expandedFolderIds: Map<string, boolean>
): Array<Layer | Folder> {
  const byId = new Map<string, Layer | Folder>();
  const collect = (items: Array<Layer | Folder>) => {
    for (const item of items) {
      byId.set(item.id, item);
      if (item.type === "folder") collect(item.layers);
    }
  };
  collect(rootLayers);

  const newRoot: Array<Layer | Folder> = [];
  const folderChildren = new Map<string, Layer[]>();

  for (const { id, folderId } of order) {
    const item = byId.get(id);
    if (!item) continue;

    if (folderId) {
      if (!folderChildren.has(folderId)) folderChildren.set(folderId, []);
      // Folders can't be nested, so anything reconstructed as a folder's child must be a layer.
      if (item.type === "layer") folderChildren.get(folderId)?.push(item);
    } else {
      newRoot.push(item);
    }
  }

  return newRoot.map((item) => {
    if (item.type !== "folder" || !expandedFolderIds.get(item.id)) return item;
    return { ...item, layers: folderChildren.get(item.id) ?? [] };
  });
}
